import { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import orderServices from "../../services/orders/orderservice";
import { POSModel } from "../../models/orders/order-list-model";
import posService from "../../services/pos/pos.service";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";

interface PosMovementSearchProps {
  setLoading: (loading: boolean) => void;
  onSearch: (data: any[]) => void;
  onSummary: (data: any[]) => void;
}

const movementTypeOptions = [
  { label: "Inward", value: 1 },
  { label: "Outward", value: 2 },
  { label: "Adjust", value: 3 },
];

// Alias for manufacturing unit label shown in dropdown
const aliasMuLabel = (row: any): string => {
  const code = String(row?.posCode ?? "").toUpperCase();
  const name = String(row?.posName ?? row?.name ?? "").toUpperCase();
  const addr2 = String(row?.address2 ?? row?.Address2 ?? "").toUpperCase();

  // Map by posCode first
  if (code.includes("POS-003") || code.includes("POS003")) return "Unit-1-KR";
  if (code.includes("POS-004") || code.includes("POS004")) return "Unit-2-MT";

  // Also try by place hints (defensive)
  if (name.includes("K R NAGAR") || addr2.includes("K R NAGAR") || addr2.includes("KR NAGAR"))
    return "Unit-1-KR";
  if (name.includes("MAGADI") || addr2.includes("MAGADI")) return "Unit-2-MT";

  // Fallback to original
  return row?.posCode ?? row?.posName ?? `Unit-${row?.posID ?? ""}`;
};

const RawMaterialMovementSearch: React.FC<PosMovementSearchProps> = ({
  setLoading,
  onSearch,
  onSummary,
}) => {
  const toast = useRef<Toast>(null);

  const [posList, setPosList] = useState<POSModel[]>([]);
  const [selectedPosId, setSelectedPosId] = useState<number | null>(null);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [movementTypes, setMovementTypes] = useState<number[]>([1, 2, 3]);

  // Build dropdown options with alias labels
  const posOptions = useMemo(
    () =>
      (posList ?? []).map((p: any) => ({
        ...p,
        label: aliasMuLabel(p),
        value: p.posID,
      })),
    [posList]
  );

  useEffect(() => {
    const fetchPOS = async () => {
      try {
        const response = await RawMaterialService.GetManufacrturingunitlist();
        setPosList(response || []);
        if (Array.isArray(response) && response.length === 1) {
          const singlePOSId = response[0].posID;
          setSelectedPosId(singlePOSId);
        }
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load POS data.",
        });
      }
    };
    fetchPOS();
  }, []);

  const handleSearch = async () => {
    if (!selectedPosId) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Manufacturing Unit",
      });
      return;
    }

    if (!startDate && !endDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Start date and End date",
      });
      return;
    }

    if (!startDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Start date",
      });
      return;
    }

    if (!endDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the End date",
      });
      return;
    }

    if (movementTypes.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select at least one movement type",
      });
      return;
    }

    setLoading(true);

    const adjustedStartDate = new Date(startDate);
    adjustedStartDate.setHours(0, 0, 0, 0);

    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);

    // ISO-like, stable sort-safe format
    const formatToSQLDateTime = (date: Date) => date.toLocaleString("sv-SE").replace(" ", "T");

    const payload = {
      posId: selectedPosId,
      startDate: formatToSQLDateTime(adjustedStartDate),
      endDate: formatToSQLDateTime(adjustedEndDate),
      movementTypes: movementTypes,
    };

    try {
      const result = await RawMaterialService.GetRawMaterialMovementLog(payload);
      if (result?.movemementresult) {
        onSearch(result.movemementresult);
      } else {
        toast.current?.show({
          severity: "info",
          summary: "No data",
          detail: "No movement records found.",
        });
        onSearch([]);
      }

      // Summary
      const Result1 = await RawMaterialService.GetRawMaterialStockSummary(payload);
      if (Result1?.summaryresult) {
        onSummary(Result1.summaryresult);
      } else {
        onSummary([]);
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch movement logs or summary.",
      });
      onSearch([]);
      onSummary([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (posList.length === 1) {
      setSelectedPosId(posList[0].posID); // Auto-select if only 1 MU
    } else {
      setSelectedPosId(null);
    }
    setStartDate(null);
    setEndDate(null);
    onSearch([]);
    onSummary([]);
  };

  return (
    <div className="card border-noround p-3 w-full">
      <Toast ref={toast} />

      <div className="grid">
        {/* Manufacturing Unit Dropdown (with aliases) */}
        <div className="col-12 md:col-3">
          <Dropdown
            value={selectedPosId}
            options={posOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Manufacturing Unit"
            onChange={(e: DropdownChangeEvent) => setSelectedPosId(e.value)}
            className="w-full"
            disabled={posOptions.length === 1}
          />
        </div>

        {/* Start Date */}
        <div className="col-6 md:col-2">
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate(e.value as Date | null)}
            showButtonBar
            dateFormat="dd/mm/yy"
            placeholder="Start Date"
            className="w-full"
            readOnlyInput
          />
        </div>

        {/* End Date */}
        <div className="col-6 md:col-2">
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate(e.value as Date | null)}
            showButtonBar
            dateFormat="dd/mm/yy"
            placeholder="End Date"
            className="w-full"
            readOnlyInput
          />
        </div>

        {/* Movement Types */}
        <div className="col-12 md:col-2 flex flex-wrap gap-3 align-items-center">
          <MultiSelect
            value={movementTypes}
            options={movementTypeOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Movement Types"
            className="w-full"
            onChange={(e) => setMovementTypes(e.value)}
            display="chip"
          />
        </div>

        {/* Buttons (removed the spacer so they sit right after Movement Types) */}
        <div className="col-6 md:col-1">
          <Button label="Filter" onClick={handleSearch} severity="success" className="w-full" />
        </div>
        <div className="col-6 md:col-1">
          <Button label="Reset" onClick={handleReset} severity="secondary" className="w-full" text raised />
        </div>
      </div>
    </div>
  );
};

export default RawMaterialMovementSearch;